import { useState } from "react";
import { theme, FONT_RUNE } from "./theme";
import { createGame, connect, type NetClient } from "../net/client";
import type { GameState, Side, VictoryConfig } from "../engine";
import { attachOnce } from "./online-attach";

export interface OnlineSession {
  client: NetClient;
  mySide: Side;
  code: string;
  initialState: GameState;
  rejoinToken: string;
}

export function OnlineLobby({ victoryConfig, onConnected, onCancel }: {
  victoryConfig: VictoryConfig;
  onConnected: (session: OnlineSession) => void;
  onCancel: () => void;
}) {
  const [view, setView] = useState<"start" | "create" | "join" | "waiting">("start");
  const [code, setCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    setError(null);
    setView("create");
    try {
      const { code: newCode } = await createGame(victoryConfig);
      setCode(newCode);
      setView("waiting");
      const client = connect(newCode);
      attachOnce(client, newCode, onConnected, setError);
    } catch (e) {
      setError((e as Error).message);
      setView("start");
    }
  };

  const handleJoin = () => {
    setError(null);
    if (joinCode.length < 4) {
      setError("enter a valid code");
      return;
    }
    const client = connect(joinCode);
    setView("waiting");
    setCode(joinCode);
    attachOnce(client, joinCode, onConnected, setError);
  };

  return (
    <div style={{
      width: "100%",
      height: "100%",
      background: theme.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <div style={{ maxWidth: 400, width: "calc(100% - 36px)", padding: 22 }}>
        <div className="rune" style={{ color: theme.accentSoft, fontSize: 12, letterSpacing: 3 }}>ONLINE</div>
        <h2 className="rune" style={{ color: theme.parchment, fontSize: 26, margin: "4px 0 22px" }}>Play with a friend</h2>
        {error && (
          <div style={{ color: theme.captureFlash, fontSize: 13, marginBottom: 12 }}>{error}</div>
        )}

        {view === "start" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={handleCreate} style={primary}>Create game</button>
            <div style={{ height: 8 }} />
            <input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="Enter code (e.g. K7H3W2)"
              style={input}
              autoFocus
            />
            <button onClick={handleJoin} style={primary}>Join game</button>
            <div style={{ height: 8 }} />
            <button onClick={onCancel} style={secondary}>Back</button>
          </div>
        )}

        {view === "waiting" && (
          <div style={{ textAlign: "center" }}>
            <div className="rune" style={{ color: theme.boardLabel, fontSize: 12, letterSpacing: 2 }}>YOUR CODE</div>
            <div className="rune" style={{
              color: theme.accent,
              fontSize: 36,
              letterSpacing: 6,
              margin: "8px 0 18px",
              padding: "10px 16px",
              border: `1px solid ${theme.accent}`,
              borderRadius: 6,
              background: theme.bgElev,
            }}>
              {code}
            </div>
            <div style={{ color: theme.parchment, fontSize: 14, opacity: 0.85 }}>
              Share this code with your opponent. Waiting for them to join…
            </div>
            <button onClick={onCancel} style={{ ...secondary, marginTop: 24 }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

const primary: React.CSSProperties = {
  padding: "10px 16px",
  background: theme.accent,
  color: theme.bg,
  border: `1px solid ${theme.accent}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 16,
  cursor: "pointer",
};

const secondary: React.CSSProperties = {
  padding: "8px 14px",
  background: theme.boardEdge,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 14,
  cursor: "pointer",
};

const input: React.CSSProperties = {
  padding: "10px 14px",
  background: theme.bgElev,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 18,
  letterSpacing: 4,
  textAlign: "center",
  outline: "none",
};
